import { useEffect, useRef, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import {
  BrowserMultiFormatReader,
  BarcodeFormat,
  DecodeHintType,
  RGBLuminanceSource,
  BinaryBitmap,
  HybridBinarizer,
} from "@zxing/library";

import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";

import MenuSearchMateriais from "pages/components/MenuSearchMateriais";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import CloseIcon from "@mui/icons-material/Close";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

const descreverConversaoEAN = (item) => {
  const quantidade = Number(item?.quantidade ?? 1);
  const unidades = Number(item?.unidades_conversao ?? 0);

  const tipo = ["A", "B", "C"].includes(item?.tipo_conversao)
    ? item.tipo_conversao
    : quantidade === 1
    ? "A"
    : quantidade < 1
    ? "B"
    : "C";

  if (tipo === "A") {
    return {
      titulo: "A — Material completo",
      descricao: "Este EAN corresponde a 1 unidade completa do material.",
    };
  }

  if (tipo === "B") {
    return {
      titulo: "B — Material incompleto",
      descricao:
        unidades > 1
          ? `${unidades} artigos deste EAN formam 1 unidade do material.`
          : "Este EAN corresponde a uma parte da unidade do material.",
    };
  }

  return {
    titulo: "C — Conjunto de unidades",
    descricao:
      unidades > 1
        ? `Este EAN contém ${unidades} unidades completas do material.`
        : `Este EAN representa ${quantidade} unidades do material.`,
  };
};

const formatarCodigoVisual = (valor) => {
  const s = String(valor || "").replace(/\s+/g, "");
  if (!s) return "";

  const grupos = [];
  let i = s.length;

  while (i > 0) {
    const ini = Math.max(0, i - 6);
    grupos.unshift(s.slice(ini, i));
    i = ini;
  }

  return grupos.join(" ");
};

const lerComZxing = async (video) => {
  const canvas = document.createElement("canvas");
  const escala = 2;

  canvas.width = video.videoWidth * escala;
  canvas.height = video.videoHeight * escala;

  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

  const dataUrl = canvas.toDataURL("image/png");

  const img = new Image();
  img.src = dataUrl;

  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
  });

  const reader = new BrowserMultiFormatReader();
  const resultado = await reader.decodeFromImageElement(img);

  return {
    codigo: resultado?.getText?.() || "",
    tipo: resultado?.getBarcodeFormat ? String(resultado.getBarcodeFormat()) : "zxing",
  };
};

const isQrCode = (tipo) =>
  String(tipo || "")
    .toLowerCase()
    .includes("qr");

const isLinkFichaProduto = (valor) => {
  const s = String(valor || "")
    .trim()
    .toLowerCase();
  return s.includes("/produto/");
};

export default function DialogBarcodeReader({
  open,
  onClose,
  onMaterialDetected = null,
  permitirAssociacoes = false,
  selecionarExplicitamente = false,
  usarCamera = true,
  codigoInicial = null,
}) {
  const { showAlert } = useAlert();
  const { userData } = useAuth();
  const idutilizador = Number(userData?.id || 0);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const codigoInputRef = useRef(null);
  const materialInputRef = useRef(null);
  const html5QrRef = useRef(null);
  const pesquisaEANTimeoutRef = useRef(null);
  const pesquisaEANSeqRef = useRef(0);
  const cameraParadaPorLeituraRef = useRef(false);
  const dialogAbertoAnteriorRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [modoCameraInterno, setModoCameraInterno] = useState(usarCamera);
  const [codigo, setCodigo] = useState("");
  const [tipoCodigo, setTipoCodigo] = useState("");
  const [resultado, setResultado] = useState(null);
  const [suportaDetector, setSuportaDetector] = useState(false);

  const [materiais, setMateriais] = useState([]);
  const [identificadoresMateriais, setIdentificadoresMateriais] = useState({});
  const [identificadorEditar, setIdentificadorEditar] = useState(null);
  const [tipoConversaoEditar, setTipoConversaoEditar] = useState("A");
  const [quantidadeEditar, setQuantidadeEditar] = useState("1");
  const [guardarQuantidadeLoading, setGuardarQuantidadeLoading] = useState(false);

  // Confirmação de remoção de uma associação EAN.
  const [confirmarRemocaoAssociacao, setConfirmarRemocaoAssociacao] = useState(false);
  const [removerAssociacaoLoading, setRemoverAssociacaoLoading] = useState(false);

  const pesquisaMateriaisSeq = useRef(0);

  const [materialSelecionado, setMaterialSelecionado] = useState(null);
  const [materialInput, setMaterialInput] = useState("");
  const [tipoConversaoAssoc, setTipoConversaoAssoc] = useState("A");
  const [quantidadeAssoc, setQuantidadeAssoc] = useState("1");
  const [associarLoading, setAssociarLoading] = useState(false);

  const [modoOperacao, setModoOperacao] = useState("");
  const [quantidadeMov, setQuantidadeMov] = useState("1");
  const [documento, setDocumento] = useState("");
  const [localizacao, setLocalizacao] = useState("");

  const [openMaterialDialog, setOpenMaterialDialog] = useState(false);
  const [materialDialog, setMaterialDialog] = useState(null);

  const [openConfirmarSubstituicao, setOpenConfirmarSubstituicao] = useState(false);
  const [dadosSubstituicao, setDadosSubstituicao] = useState(null);

  const [associacoesEncontradas, setAssociacoesEncontradas] = useState([]);
  const [mostrarNovaAssociacao, setMostrarNovaAssociacao] = useState(false);

  const carregarMateriais = async (texto) => {
    const q = (texto || "").trim();
    const seq = ++pesquisaMateriaisSeq.current;

    setIdentificadoresMateriais({});

    if (q.length < 2) {
      setMateriais([]);
      return;
    }

    const [data, err] = await handleApi(
      api.post("Pesquisa/QuickSearch", {
        q,
        limit: 30,
        refvendedor: "",
        idutilizador,
      })
    );

    if (seq !== pesquisaMateriaisSeq.current) return;

    if (err) {
      setMateriais([]);
      return;
    }

    const lista = Array.isArray(data)
      ? data.filter((x) => String(x?.kind || "").toLowerCase() === "material")
      : [];

    setMateriais(lista);

    const refs = [
      ...new Set(lista.map((x) => String(x?.refmaterial || x?.ref || "").trim()).filter(Boolean)),
    ];

    if (refs.length === 0) return;

    const [identificadores, erroIdentificadores] = await handleApi(
      api.post("Materiais/SelMateriaisIdentificadoresAtivos", {
        refs,
      })
    );

    if (seq !== pesquisaMateriaisSeq.current) return;

    if (erroIdentificadores) {
      console.error("Erro ao consultar identificadores:", erroIdentificadores);
      return;
    }

    const mapa = {};

    (Array.isArray(identificadores) ? identificadores : []).forEach((item) => {
      const ref = String(item.refmaterial || "").trim();

      if (!mapa[ref]) mapa[ref] = [];

      mapa[ref].push(item);
    });

    setIdentificadoresMateriais(mapa);
  };

  useEffect(() => {
    if (open && mostrarNovaAssociacao && !materialSelecionado) {
      materialInputRef.current?.focus();
    }
  }, [open, mostrarNovaAssociacao]);

  const removerAssociacaoIdentificador = async () => {
    if (!identificadorEditar?.id || removerAssociacaoLoading) return;

    const associacao = identificadorEditar;

    setRemoverAssociacaoLoading(true);

    try {
      const [, err, meta] = await handleApi(
        api.post("Materiais/UpdMaterialIdentificador", {
          id: associacao.id,
          inativo: 1,
        })
      );

      if (err || meta?.success === false) {
        showAlert(err || meta?.message || "Não foi possível remover a associação.", "error");
        return;
      }

      // Retirar apenas esta associação dos resultados apresentados.
      setAssociacoesEncontradas((anterior) => anterior.filter((item) => item.id !== associacao.id));

      setIdentificadoresMateriais((anterior) => {
        const atualizado = {};

        Object.entries(anterior).forEach(([ref, lista]) => {
          atualizado[ref] = lista.filter((item) => item.id !== associacao.id);
        });

        return atualizado;
      });

      setConfirmarRemocaoAssociacao(false);
      setIdentificadorEditar(null);
      setTipoConversaoEditar("A");
      setQuantidadeEditar("1");

      showAlert("Associação EAN removida com sucesso.", "success");
    } catch (error) {
      showAlert(error?.message || "Erro ao remover a associação EAN.", "error");
    } finally {
      setRemoverAssociacaoLoading(false);
    }
  };

  const guardarQuantidadeIdentificador = async () => {
    if (!identificadorEditar || guardarQuantidadeLoading) return;

    const tipoConversao = tipoConversaoEditar;

    const unidadesConversao = tipoConversao === "A" ? 1 : Number(String(quantidadeEditar).trim());

    if (
      !["A", "B", "C"].includes(tipoConversao) ||
      !Number.isSafeInteger(unidadesConversao) ||
      (tipoConversao === "A" && unidadesConversao !== 1) ||
      (tipoConversao !== "A" && unidadesConversao <= 1) ||
      unidadesConversao > 2147483647
    ) {
      showAlert(
        "Indica um número inteiro de unidades válido e superior a 1 para os tipos B ou C.",
        "warning"
      );
      return;
    }

    setGuardarQuantidadeLoading(true);

    try {
      const [, err, meta] = await handleApi(
        api.post("Materiais/UpdMaterialIdentificador", {
          id: identificadorEditar.id,
          tipo_conversao: tipoConversao,
          unidades_conversao: unidadesConversao,
        })
      );

      if (err) {
        showAlert(err, "error");
        return;
      }

      // Atualizar a informação local.
      // O valor decimal é apenas para apresentação;
      // a relação exata fica guardada na API.
      const quantidade =
        tipoConversao === "A"
          ? 1
          : tipoConversao === "B"
          ? Number((1 / unidadesConversao).toFixed(12))
          : unidadesConversao;

      setIdentificadoresMateriais((anterior) => {
        const atualizado = {};

        Object.entries(anterior).forEach(([ref, lista]) => {
          atualizado[ref] = lista.map((item) =>
            item.id === identificadorEditar.id
              ? {
                  ...item,
                  quantidade,
                  tipo_conversao: tipoConversao,
                  unidades_conversao: unidadesConversao,
                }
              : item
          );
        });

        return atualizado;
      });

      setAssociacoesEncontradas((anterior) =>
        anterior.map((item) =>
          item.id === identificadorEditar.id
            ? {
                ...item,
                quantidade,
                tipo_conversao: tipoConversao,
                unidades_conversao: unidadesConversao,
              }
            : item
        )
      );

      setIdentificadorEditar(null);
      setTipoConversaoEditar("A");
      setQuantidadeEditar("1");

      showAlert(meta?.message || "Conversão do EAN atualizada com sucesso.", "success");
    } catch (error) {
      showAlert(error?.message || "Erro ao atualizar a conversão do EAN.", "error");
    } finally {
      setGuardarQuantidadeLoading(false);
    }
  };

  const normalizarQuantidade = (valor) => {
    const txt = String(valor || "")
      .replace(",", ".")
      .trim();
    const n = Number(txt);
    return Number.isFinite(n) && n > 0 ? n : 1;
  };

  const pararCamera = () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current) videoRef.current.srcObject = null;
    } catch {}
  };

  const pararHtml5Qr = async () => {
    try {
      if (html5QrRef.current) {
        const qr = html5QrRef.current;

        try {
          if (qr.isScanning) {
            await qr.stop();
          }
        } catch {}

        try {
          await qr.clear();
        } catch {}

        html5QrRef.current = null;
      }
    } catch {}
  };

  const iniciarHtml5Qr = async () => {
    try {
      await pararHtml5Qr();

      const qr = new Html5Qrcode("html5-reader", {
        formatsToSupport: [
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
          Html5QrcodeSupportedFormats.ITF,
          Html5QrcodeSupportedFormats.QR_CODE,
        ],
      });

      html5QrRef.current = qr;

      await qr.start(
        { facingMode: "environment" },
        {
          fps: 8,
          //qrbox: { width: 340, height: 180 },
        },
        async (decodedText, decodedResult) => {
          if (!decodedText) return;

          cameraParadaPorLeituraRef.current = true;
          await pararHtml5Qr();

          const cod = String(decodedText || "").trim();
          const tipo = decodedResult?.result?.format?.formatName || "html5-qrcode";

          setCodigo(cod);
          setTipoCodigo(tipo);

          setResultado(null);
          setMaterialSelecionado(null);
          setMaterialInput("");
          setMateriais([]);
          setQuantidadeAssoc("1");

          showAlert(`Código detetado: ${cod}`, "success");

          await pesquisarCodigo(cod, tipo);
        },
        () => {}
      );
    } catch (err) {
      console.error("ERRO HTML5-QRCODE:", err);
      showAlert(`Erro ao iniciar leitor compatível: ${err?.message || err}`, "error");
    }
  };

  const lerComHtml5Qrcode = async (video) => {
    const canvas = document.createElement("canvas");
    const escala = 2;

    canvas.width = video.videoWidth * escala;
    canvas.height = video.videoHeight * escala;

    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/png", 1);
    });

    if (!blob) {
      throw new Error("Não foi possível capturar imagem da câmera.");
    }

    const file = new File([blob], "barcode.png", { type: "image/png" });

    await pararHtml5Qr?.();

    const qr = new Html5Qrcode("html5-reader", {
      formatsToSupport: [
        Html5QrcodeSupportedFormats.EAN_13,
        Html5QrcodeSupportedFormats.EAN_8,
        Html5QrcodeSupportedFormats.UPC_A,
        Html5QrcodeSupportedFormats.UPC_E,
        Html5QrcodeSupportedFormats.CODE_128,
        Html5QrcodeSupportedFormats.CODE_39,
        Html5QrcodeSupportedFormats.ITF,
        Html5QrcodeSupportedFormats.QR_CODE,
      ],
    });

    html5QrRef.current = qr;

    const decodedText = await qr.scanFile(file, false);

    try {
      await qr.clear();
    } catch {}

    html5QrRef.current = null;

    return {
      codigo: decodedText || "",
      tipo: "html5-qrcode",
    };
  };

  const limpar = () => {
    if (pesquisaEANTimeoutRef.current) {
      clearTimeout(pesquisaEANTimeoutRef.current);
      pesquisaEANTimeoutRef.current = null;
    }

    setCodigo("");
    setTipoCodigo("");
    setResultado(null);
    setLoading(false);
    setCameraLoading(false);
    setMateriais([]);
    setMaterialSelecionado(null);
    setMaterialInput("");
    setQuantidadeAssoc("1");
    setAssociarLoading(false);
    setModoOperacao("");
    setQuantidadeMov("1");
    setDocumento("");
    setLocalizacao("");
    setAssociacoesEncontradas([]);
    setMostrarNovaAssociacao(false);
  };

  const fechar = async () => {
    await pararHtml5Qr();
    pararCamera();
    limpar();
    onClose?.();
  };

  const iniciarCamera = async () => {
    setCameraLoading(true);

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        showAlert("Este browser não permite acesso à câmera neste contexto.", "warning");
        setCameraLoading(false);
        return;
      }

      pararCamera();

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      console.error("ERRO ZXING:", err);

      showAlert(`Erro ao tentar ler o código: ${err?.message || err}`, "error");
    } finally {
      setCameraLoading(false);
    }
  };

  const limparCodigoEAN = async () => {
    // Invalidar eventuais pesquisas ainda em curso.
    pesquisaEANSeqRef.current += 1;

    if (pesquisaEANTimeoutRef.current) {
      clearTimeout(pesquisaEANTimeoutRef.current);
      pesquisaEANTimeoutRef.current = null;
    }

    setCodigo("");
    setTipoCodigo("");
    setResultado(null);
    setAssociacoesEncontradas([]);
    setMostrarNovaAssociacao(false);
    setMaterialSelecionado(null);
    setMaterialInput("");
    setMateriais([]);
    setIdentificadoresMateriais({});
    setTipoConversaoAssoc("A");
    setQuantidadeAssoc("1");

    if (open && modoCameraInterno && cameraParadaPorLeituraRef.current) {
      cameraParadaPorLeituraRef.current = false;

      await pararHtml5Qr();
      await iniciarHtml5Qr();
    }

    codigoInputRef.current?.focus();
  };

  // Ao reabrir o leitor para alterar uma associação,
  // recuperar o código EAN anteriormente selecionado.
  useEffect(() => {
    if (!open || !codigoInicial) return;

    const cod = String(codigoInicial).trim();
    if (!cod) return;

    setCodigo(cod);
    setTipoCodigo("");

    // Recuperar as associações existentes sem voltar a ler o EAN.
    pesquisarCodigo(cod, "");
  }, [open, codigoInicial]);

  const pesquisarCodigo = async (codigoArg = null, tipoArg = null) => {
    const cod = (codigoArg ?? codigo ?? "").trim();
    const tipo = (tipoArg ?? tipoCodigo ?? "").trim();

    if (!onMaterialDetected && isQrCode(tipo) && isLinkFichaProduto(cod)) {
      await pararHtml5Qr();
      pararCamera();
      window.open(cod, "_blank", "noopener,noreferrer");
      return;
    }

    if (!cod) {
      showAlert("Indica ou lê um código.", "warning");
      return;
    }

    const seq = ++pesquisaEANSeqRef.current;

    setLoading(true);
    setResultado(null);

    const [data, err, meta] = await handleApi(
      api.post("Materiais/SelMaterialIdentificadorPorCodigo", {
        codigo: cod,
        tipo_codigo: tipo,
      })
    );

    // Ignorar respostas de pesquisas que já foram substituídas.
    if (seq !== pesquisaEANSeqRef.current) return;

    setLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    const lista = Array.isArray(data) ? data : [];

    setAssociacoesEncontradas(lista);

    if (onMaterialDetected) {
      if (lista.length === 0) {
        if (permitirAssociacoes) {
          setMostrarNovaAssociacao(true);
          await pararHtml5Qr();
          pararCamera();

          showAlert(
            "Este código ainda não está associado. Podes criar uma nova associação.",
            "warning"
          );
        } else {
          setMostrarNovaAssociacao(false);
          showAlert(
            "Este código ainda não está associado a nenhum material. Seleciona o material pela tabela.",
            "warning"
          );
        }

        return;
      }

      if (lista.length === 1 && !selecionarExplicitamente) {
        await fechar();
        onMaterialDetected(lista[0]);
        return;
      }

      // Havendo várias associações, o utilizador escolhe uma.
      setMostrarNovaAssociacao(false);
      await pararHtml5Qr();
      pararCamera();
      return;
    }

    if (lista.length > 0) {
      setResultado(null);
      setMostrarNovaAssociacao(false);

      setMaterialSelecionado(null);
      setMaterialInput("");
      setMateriais([]);
      setQuantidadeAssoc("1");
      setModoOperacao("");
      setQuantidadeMov("1");
      setDocumento("");
      setLocalizacao("");

      if (isQrCode(tipo)) {
        return;
      }

      await pararHtml5Qr();
      pararCamera();
    } else {
      setResultado(null);
      setAssociacoesEncontradas([]);
      setMostrarNovaAssociacao(true);
    }

    if (meta?.message) {
      showAlert(meta.message, meta.severity || "success");
    }
  };

  const associarCodigoMaterial = async () => {
    const cod = (codigo || "").trim();

    if (!cod) {
      showAlert("Lê ou indica primeiro um código.", "warning");
      return;
    }

    const refmaterial = String(
      materialSelecionado?.refmaterial || materialSelecionado?.ref || ""
    ).trim();

    if (!refmaterial) {
      showAlert("Seleciona o material a associar.", "warning");
      return;
    }

    const tipoConversao = tipoConversaoAssoc;

    const unidadesConversao = tipoConversao === "A" ? 1 : Number(String(quantidadeAssoc).trim());

    if (
      !["A", "B", "C"].includes(tipoConversao) ||
      !Number.isSafeInteger(unidadesConversao) ||
      (tipoConversao === "A" && unidadesConversao !== 1) ||
      (tipoConversao !== "A" && unidadesConversao <= 1) ||
      unidadesConversao > 2147483647
    ) {
      showAlert(
        "Indica um número inteiro de unidades superior a 1 para os tipos B ou C.",
        "warning"
      );
      return;
    }

    setAssociarLoading(true);

    const [data, err, meta] = await handleApi(
      api.post("Materiais/InsMaterialIdentificador", {
        codigo: cod,
        tipo_codigo: (tipoCodigo || "").trim(),
        refmaterial,
        tipo_conversao: tipoConversao,
        unidades_conversao: unidadesConversao,
        descricao: materialSelecionado?.title || materialSelecionado?.nome || "",
        origem: "LEITOR_NAVBAR",
      })
    );

    setAssociarLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    if (data?.requerConfirmacaoSubstituicao) {
      setDadosSubstituicao(data);
      setOpenConfirmarSubstituicao(true);
      return;
    }

    if (meta?.message) {
      showAlert(meta.message, meta.severity || "success");
    }

    await pesquisarCodigo(cod, tipoCodigo);
  };

  const confirmarSubstituicao = async () => {
    if (associarLoading || !dadosSubstituicao?.novo) return;

    const novaAssociacao = dadosSubstituicao.novo;

    setAssociarLoading(true);

    try {
      const [data, err, meta] = await handleApi(
        api.post("Materiais/InsMaterialIdentificador", {
          codigo: novaAssociacao.codigo,
          tipo_codigo: novaAssociacao.tipo_codigo,
          refmaterial: novaAssociacao.refmaterial,
          quantidade: novaAssociacao.quantidade,
          descricao: novaAssociacao.descricao || "",
          origem: novaAssociacao.origem || "LEITOR_NAVBAR",
          forcar_substituicao: 1,
        })
      );

      if (err) {
        showAlert(err, "error");
        return;
      }

      if (data?.requerConfirmacaoSubstituicao) {
        showAlert("A substituição continua a necessitar de confirmação.", "warning");
        return;
      }

      setOpenConfirmarSubstituicao(false);
      setDadosSubstituicao(null);

      showAlert(meta?.message || "Código substituído com sucesso.", "success");

      await pesquisarCodigo(novaAssociacao.codigo, novaAssociacao.tipo_codigo);
    } catch (error) {
      showAlert(error?.message || "Erro ao substituir o código.", "error");
    } finally {
      setAssociarLoading(false);
    }
  };

  const corrigirAssociacao = async () => {
    if (!resultado?.id) {
      showAlert("Não foi encontrada uma associação ativa para corrigir.", "warning");
      return;
    }

    setAssociarLoading(true);

    const [data, err, meta] = await handleApi(
      api.post("Materiais/UpdMaterialIdentificador", {
        id: resultado.id,
        quantidade: resultado.quantidade || 1,
        descricao: resultado.descricao || "",
        inativo: 1,
      })
    );

    setAssociarLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    if (meta?.message) {
      showAlert("Associação anterior inativada. Escolhe agora o material correto.", "warning");
    }

    setResultado(null);
    setMaterialSelecionado(null);
    setMaterialInput("");
    setMateriais([]);
    setQuantidadeAssoc(String(resultado.quantidade || 1));
  };

  const capturarELer = async () => {
    try {
      const video = videoRef.current;

      if (!video || !video.videoWidth || !video.videoHeight) {
        showAlert("A imagem da câmera ainda não está disponível.", "warning");
        return;
      }

      let cod = "";
      let tipo = "";

      if ("BarcodeDetector" in window) {
        const detector = new window.BarcodeDetector({
          formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "itf", "qr_code"],
        });

        const resultados = await detector.detect(video);

        if (resultados && resultados.length > 0) {
          cod = resultados[0].rawValue || "";
          tipo = resultados[0].format || "";
        }
      } else {
        const lido = await lerComHtml5Qrcode(video);
        cod = lido.codigo;
        tipo = lido.tipo;
      }

      if (!cod) {
        showAlert("Não foi detetado nenhum código nesta imagem.", "warning");
        return;
      }

      setCodigo(cod);
      setTipoCodigo(tipo);

      setResultado(null);
      setMaterialSelecionado(null);
      setMaterialInput("");
      setMateriais([]);
      setQuantidadeAssoc("1");

      showAlert(`Código detetado: ${cod}`, "success");

      await pesquisarCodigo(cod, tipo);
    } catch (err) {
      console.error(err);
      showAlert("Erro ao tentar ler o código.", "error");
    }
  };

  useEffect(() => {
    if (open && !dialogAbertoAnteriorRef.current) {
      setModoCameraInterno(usarCamera);
      cameraParadaPorLeituraRef.current = false;
    }

    dialogAbertoAnteriorRef.current = open;
  }, [open, usarCamera]);

  useEffect(() => {
    if (!open) return undefined;

    const suporta = "BarcodeDetector" in window;

    setSuportaDetector(suporta);

    // Iniciar a câmara apenas no modo Câmara.
    if (modoCameraInterno) {
      cameraParadaPorLeituraRef.current = false;
      iniciarHtml5Qr();
    }

    setTimeout(() => {
      codigoInputRef.current?.focus();
    }, 300);

    return () => {
      if (pesquisaEANTimeoutRef.current) {
        clearTimeout(pesquisaEANTimeoutRef.current);
        pesquisaEANTimeoutRef.current = null;
      }

      pararHtml5Qr();
      pararCamera();
    };
  }, [open, modoCameraInterno]);

  return (
    <>
      <Dialog open={open && !openMaterialDialog} onClose={fechar} maxWidth="sm" fullWidth>
        <DialogTitle>Leitor de código</DialogTitle>

        <DialogContent dividers>
          {(loading || cameraLoading) && (
            <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
              <CircularProgress size={28} />
            </MDBox>
          )}

          <MDBox display="grid" gap={1.5}>
            <MDBox display="grid" gridTemplateColumns={{ xs: "1fr 1fr" }} gap={1}>
              <MDButton
                color="info"
                variant={modoCameraInterno ? "gradient" : "outlined"}
                startIcon={<PhotoCameraIcon />}
                onClick={() => {
                  if (!modoCameraInterno) {
                    cameraParadaPorLeituraRef.current = false;
                    setModoCameraInterno(true);
                  }
                }}
                sx={{
                  minHeight: 48,
                  px: 1,
                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                }}
              >
                Câmara
              </MDButton>

              <MDButton
                color="dark"
                variant={!modoCameraInterno ? "gradient" : "outlined"}
                startIcon={<DocumentScannerIcon />}
                onClick={() => {
                  if (modoCameraInterno) {
                    cameraParadaPorLeituraRef.current = false;
                    setModoCameraInterno(false);
                  }

                  codigoInputRef.current?.focus();
                }}
                sx={{
                  minHeight: 48,
                  px: 1,
                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                }}
              >
                Leitor Barcode
              </MDButton>
            </MDBox>

            <MDTypography variant="button" color={suportaDetector ? "success" : "text"}>
              {suportaDetector
                ? "Leitura nativa disponível."
                : "Leitura por compatibilidade disponível."}
            </MDTypography>

            {modoCameraInterno && (
              <div
                id="html5-reader"
                style={{
                  width: "100%",
                  minHeight: 260,
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              />
            )}

            <MDTypography variant="button" color="text">
              {modoCameraInterno
                ? "Aponta a câmera ao código. A leitura inicia automaticamente."
                : "Lê o código com o leitor Barcode. O EAN será identificado automaticamente."}
            </MDTypography>

            {/* {suportaDetector ? (
            <MDButton
              color="info"
              onClick={capturarELer}
              disabled={loading || cameraLoading}
              fullWidth
            >
              Capturar e ler
            </MDButton>
          ) : null} */}

            <MDBox>
              <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>
                EAN (código de barras)
              </div>
              <TextField
                inputRef={codigoInputRef}
                autoFocus
                size="small"
                fullWidth
                value={codigo}
                onChange={(e) => {
                  const valor = e.target.value.replace(/\s+/g, "");

                  if (!valor) {
                    limparCodigoEAN();
                    return;
                  }

                  setCodigo(valor);

                  // Invalidar imediatamente qualquer pesquisa do código anterior.
                  pesquisaEANSeqRef.current += 1;
                  setLoading(false);

                  // Cancelar a pesquisa anterior enquanto o utilizador escreve.
                  if (pesquisaEANTimeoutRef.current) {
                    clearTimeout(pesquisaEANTimeoutRef.current);
                    pesquisaEANTimeoutRef.current = null;
                  }

                  // Uma alteração manual invalida os resultados do código anterior.
                  setAssociacoesEncontradas([]);
                  setMostrarNovaAssociacao(false);
                  setResultado(null);

                  // Pesquisar automaticamente 1 segundo após o último carácter,
                  // independentemente do formato ou comprimento do código.
                  if (valor.trim()) {
                    pesquisaEANTimeoutRef.current = setTimeout(() => {
                      pesquisaEANTimeoutRef.current = null;
                      pesquisarCodigo(valor, "");
                    }, 1000);
                  }

                  if (/^\d{13}$/.test(valor)) {
                    setTipoCodigo("ean_13");
                  } else if (/^\d{8}$/.test(valor)) {
                    setTipoCodigo("ean_8");
                  } else if (/^\d{12}$/.test(valor)) {
                    setTipoCodigo("upc_a");
                  } else {
                    setTipoCodigo("");
                  }
                }}
                placeholder="Ex.: 1234567890123"
                InputProps={{
                  endAdornment: codigo ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Apagar código EAN"
                        title="Apagar código EAN"
                        onClick={limparCodigoEAN}
                        edge="end"
                        sx={{
                          minWidth: 40,
                          minHeight: 40,
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              {codigo ? (
                <div
                  style={{
                    marginTop: 6,
                    padding: "8px 10px",
                    borderRadius: 8,
                    background: "#f8f9fa",
                    fontSize: 20,
                    fontWeight: 700,
                    letterSpacing: 1,
                    color: "#1a2b4c",
                    textAlign: "center",
                  }}
                >
                  {formatarCodigoVisual(codigo)}
                </div>
              ) : null}
            </MDBox>

            <MDBox>
              <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Tipo de código</div>
              {tipoCodigo ? (
                <div
                  style={{
                    marginTop: 6,
                    padding: "8px 10px",
                    borderRadius: 8,
                    background: "#f8f9fa",
                    fontSize: 16,
                    fontWeight: 600,
                    color: "#1a2b4c",
                    textAlign: "center",
                  }}
                >
                  {tipoCodigo}
                </div>
              ) : (
                <TextField
                  size="small"
                  fullWidth
                  value=""
                  disabled
                  placeholder="Será preenchido automaticamente"
                />
              )}
            </MDBox>

            {associacoesEncontradas.length > 0 ? (
              <MDBox p={1.5} borderRadius="8px" style={{ background: "#f8f9fa" }}>
                <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
                  Associações encontradas
                </MDTypography>

                <MDBox display="grid" gap={1}>
                  {associacoesEncontradas.map((item) => (
                    <MDBox
                      key={item.id}
                      p={1.25}
                      borderRadius="8px"
                      sx={{
                        cursor: "pointer",
                        border: "1px solid #dee2e6",
                        "&:hover": {
                          backgroundColor: "#eef4ff",
                        },
                      }}
                      onClick={
                        onMaterialDetected
                          ? async () => {
                              await fechar();
                              onMaterialDetected(item);
                            }
                          : undefined
                      }
                    >
                      <MDBox
                        display="flex"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        gap={1}
                      >
                        <MDTypography
                          variant="button"
                          display="block"
                          fontWeight="medium"
                          sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}
                        >
                          {item.refmaterial} - {item.nomematerial || ""}
                        </MDTypography>

                        {onMaterialDetected && selecionarExplicitamente && (
                          <MDButton
                            size="small"
                            color="info"
                            variant="outlined"
                            onClick={async (e) => {
                              e.stopPropagation();
                              await fechar();
                              onMaterialDetected(item);
                            }}
                            sx={{ flexShrink: 0, minWidth: 85 }}
                          >
                            Ver Contagens
                          </MDButton>
                        )}

                        {!onMaterialDetected && (
                          <MDButton
                            size="small"
                            color="info"
                            variant="outlined"
                            onClick={() => {
                              setMaterialDialog({
                                ref: item.refmaterial,
                                refmaterial: item.refmaterial,
                                nome: item.nomematerial || "",
                                title: `${item.refmaterial} - ${item.nomematerial || ""}`,
                              });

                              setOpenMaterialDialog(true);
                            }}
                            sx={{ flexShrink: 0, minWidth: 48 }}
                          >
                            VER
                          </MDButton>
                        )}

                        {!onMaterialDetected && (
                          <IconButton
                            size="small"
                            color="warning"
                            title="Editar conversão do EAN"
                            aria-label={`Editar conversão do EAN ${item.codigo || codigo}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();

                              const quantidade = Number(item.quantidade ?? 1);

                              const tipo = ["A", "B", "C"].includes(item.tipo_conversao)
                                ? item.tipo_conversao
                                : quantidade < 1
                                ? "B"
                                : quantidade > 1
                                ? "C"
                                : "A";

                              const unidades = item.unidades_conversao
                                ? Number(item.unidades_conversao)
                                : tipo === "B"
                                ? Math.round(1 / quantidade)
                                : tipo === "C"
                                ? Math.round(quantidade)
                                : 1;

                              setIdentificadorEditar({
                                ...item,
                                nomematerial: `${item.refmaterial} - ${item.nomematerial || ""}`,
                              });

                              setTipoConversaoEditar(tipo);
                              setQuantidadeEditar(String(unidades));
                            }}
                            sx={{
                              flexShrink: 0,
                              minWidth: 36,
                              minHeight: 36,
                            }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        )}
                      </MDBox>

                      <MDTypography
                        variant="caption"
                        color="info"
                        display="block"
                        fontWeight="bold"
                      >
                        {descreverConversaoEAN(item).titulo}
                      </MDTypography>

                      <MDTypography variant="caption" display="block" color="text">
                        {descreverConversaoEAN(item).descricao}
                      </MDTypography>

                      <MDTypography variant="caption" display="block" color="text">
                        Equivalência por leitura: {item.quantidade ?? 1}
                      </MDTypography>
                    </MDBox>
                  ))}
                </MDBox>

                {(!onMaterialDetected || permitirAssociacoes) && (
                  <MDBox mt={2}>
                    <MDButton
                      color="info"
                      variant="outlined"
                      onClick={() => {
                        setAssociacoesEncontradas([]);
                        setMostrarNovaAssociacao(true);
                      }}
                    >
                      Nova associação
                    </MDButton>
                  </MDBox>
                )}
              </MDBox>
            ) : (!onMaterialDetected || permitirAssociacoes) && codigo && mostrarNovaAssociacao ? (
              <MDBox p={1.5} borderRadius="8px" style={{ background: "#fff7ed" }}>
                <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
                  Código ainda não associado
                </MDTypography>

                <MDBox display="grid" gap={1.25}>
                  <MDBox>
                    <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Material</div>
                    <Autocomplete
                      size="small"
                      disableClearable
                      clearOnEscape
                      filterOptions={(options) => options}
                      options={materiais}
                      value={materialSelecionado}
                      inputValue={materialInput}
                      onInputChange={(_, value, reason) => {
                        setMaterialInput(value);

                        if (reason === "clear") {
                          setMaterialSelecionado(null);
                          setMateriais([]);
                          setIdentificadoresMateriais({});
                          pesquisaMateriaisSeq.current += 1;
                          return;
                        }

                        if (reason === "input") {
                          carregarMateriais(value);
                        }
                      }}
                      onChange={(_, value) => setMaterialSelecionado(value)}
                      getOptionLabel={(option) => {
                        const texto =
                          option?.label ||
                          option?.title ||
                          option?.nome ||
                          `${option?.ref || option?.refmaterial || ""}`;

                        return String(texto)
                          .split(/\s*Stock\s*:/i)[0]
                          .trim();
                      }}
                      isOptionEqualToValue={(option, value) =>
                        String(option?.ref || option?.refmaterial || "") ===
                        String(value?.ref || value?.refmaterial || "")
                      }
                      renderOption={(props, option) => {
                        const label = option?.label || option?.title || option?.nome || "";
                        const partes = String(label).split("\n");

                        return (
                          <li {...props}>
                            <MDBox>
                              <MDTypography variant="button" display="block" fontWeight="medium">
                                {partes[0] || ""}
                              </MDTypography>

                              {partes[1] ? (
                                <MDTypography
                                  variant="caption"
                                  display="block"
                                  sx={{
                                    color: "#5f6f89",
                                    fontWeight: 600,
                                  }}
                                >
                                  {partes[1]}
                                </MDTypography>
                              ) : null}

                              <MDBox mt={0.5}>
                                {(() => {
                                  const ref = String(
                                    option?.refmaterial || option?.ref || ""
                                  ).trim();
                                  const codigos = identificadoresMateriais[ref] || [];

                                  if (codigos.length === 0) {
                                    return (
                                      <MDTypography variant="caption" color="text" display="block">
                                        Sem códigos de barras associados
                                      </MDTypography>
                                    );
                                  }

                                  return codigos.map((item) => (
                                    <MDBox
                                      key={item.id}
                                      display="flex"
                                      alignItems="center"
                                      flexWrap="wrap"
                                      gap={0.5}
                                    >
                                      <MDTypography
                                        variant="caption"
                                        color="info"
                                        sx={{ fontWeight: 500 }}
                                      >
                                        {String(item.tipo_codigo || "Código").toUpperCase()}:{" "}
                                        {item.codigo}
                                        {" | "}Qtd. {item.quantidade ?? 1}
                                      </MDTypography>

                                      <IconButton
                                        size="small"
                                        color="warning"
                                        title="Editar quantidade representada"
                                        aria-label={`Editar quantidade do código ${item.codigo}`}
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();

                                          const quantidade = Number(item.quantidade ?? 1);

                                          const tipo = ["A", "B", "C"].includes(item.tipo_conversao)
                                            ? item.tipo_conversao
                                            : quantidade < 1
                                            ? "B"
                                            : quantidade > 1
                                            ? "C"
                                            : "A";

                                          const unidades = item.unidades_conversao
                                            ? Number(item.unidades_conversao)
                                            : tipo === "B"
                                            ? Math.round(1 / quantidade)
                                            : tipo === "C"
                                            ? Math.round(quantidade)
                                            : 1;

                                          setIdentificadorEditar({
                                            ...item,
                                            nomematerial: String(
                                              option?.label || option?.title || option?.nome || ""
                                            )
                                              .split(/\s*Stock\s*:/i)[0]
                                              .trim(),
                                          });
                                          setTipoConversaoEditar(tipo);
                                          setQuantidadeEditar(String(unidades));
                                        }}
                                        sx={{ padding: "3px" }}
                                      >
                                        <EditOutlinedIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </MDBox>
                                  ));
                                })()}
                              </MDBox>
                            </MDBox>
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          inputRef={materialInputRef}
                          placeholder="Pesquisar material..."
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {Boolean(materialInput) && !materialSelecionado && (
                                  <IconButton
                                    size="small"
                                    title="Limpar pesquisa de materiais"
                                    aria-label="Limpar pesquisa de materiais"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={(e) => {
                                      e.stopPropagation();

                                      pesquisaMateriaisSeq.current += 1;
                                      setMaterialInput("");
                                      setMaterialSelecionado(null);
                                      setMateriais([]);
                                      setIdentificadoresMateriais({});
                                    }}
                                    sx={{
                                      minWidth: 40,
                                      minHeight: 40,
                                      flexShrink: 0,
                                    }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                )}

                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />

                    {materialSelecionado &&
                      (() => {
                        const texto = String(
                          materialSelecionado?.label ||
                            materialSelecionado?.title ||
                            materialSelecionado?.nome ||
                            ""
                        );

                        const posicao = texto.search(/\bStock\s*:/i);

                        if (posicao < 0) return null;

                        const informacao = texto.slice(posicao).trim();

                        return (
                          <MDTypography
                            variant="caption"
                            color="text"
                            display="block"
                            mt={0.75}
                            sx={{ overflowWrap: "anywhere" }}
                          >
                            {informacao}
                          </MDTypography>
                        );
                      })()}
                  </MDBox>

                  <MDBox>
                    <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
                      O que representa este EAN (código de barras)?
                    </MDTypography>

                    <RadioGroup
                      value={tipoConversaoAssoc}
                      onChange={(e) => {
                        const tipo = e.target.value;

                        setTipoConversaoAssoc(tipo);
                        setQuantidadeAssoc(tipo === "A" ? "1" : "");
                      }}
                    >
                      <FormControlLabel
                        value="A"
                        control={<Radio size="small" />}
                        label={
                          <MDBox>
                            <MDTypography variant="button" display="block">
                              A — Material selecionado completo
                            </MDTypography>
                            <MDTypography variant="caption" color="text" display="block">
                              Este EAN corresponde a 1 unidade completa do material selecionado.
                            </MDTypography>
                          </MDBox>
                        }
                        sx={{ mb: 1, alignItems: "flex-start", "& .MuiRadio-root": { mt: 0.25 } }}
                      />

                      <FormControlLabel
                        value="B"
                        control={<Radio size="small" />}
                        label={
                          <MDBox>
                            <MDTypography variant="button" display="block">
                              B — Material selecionado incompleto
                            </MDTypography>
                            <MDTypography variant="caption" color="text" display="block">
                              Quando vários destes EAN, formam 1 unidade do material selecionado.
                            </MDTypography>
                          </MDBox>
                        }
                        sx={{ mb: 1, alignItems: "flex-start", "& .MuiRadio-root": { mt: 0.25 } }}
                      />

                      <FormControlLabel
                        value="C"
                        control={<Radio size="small" />}
                        label={
                          <MDBox>
                            <MDTypography variant="button" display="block">
                              C — Conjunto de unidades do material selecionado
                            </MDTypography>
                            <MDTypography variant="caption" color="text" display="block">
                              Quando este EAN contém várias unidades do material selecionado.
                            </MDTypography>
                          </MDBox>
                        }
                        sx={{ alignItems: "flex-start", "& .MuiRadio-root": { mt: 0.25 } }}
                      />
                    </RadioGroup>

                    {materialSelecionado && (
                      <MDBox
                        mt={1.5}
                        p={1.5}
                        borderRadius="8px"
                        sx={{ backgroundColor: "#f8f9fa" }}
                      >
                        <MDTypography variant="caption" display="block" color="text">
                          Referência selecionada
                        </MDTypography>

                        <MDTypography variant="button" fontWeight="medium" display="block">
                          {String(
                            materialSelecionado?.refmaterial || materialSelecionado?.ref || ""
                          ).trim()}
                          {" — "}
                          {materialSelecionado?.nome ||
                            materialSelecionado?.title ||
                            materialSelecionado?.label ||
                            ""}
                        </MDTypography>
                      </MDBox>
                    )}

                    {tipoConversaoAssoc === "B" && (
                      <MDBox mt={1.5}>
                        <MDTypography variant="button" display="block" mb={1}>
                          Quantos artigos deste EAN são necessários para formar 1 unidade do
                          material selecionado?
                        </MDTypography>

                        <TextField
                          size="small"
                          fullWidth
                          type="number"
                          value={quantidadeAssoc}
                          onChange={(e) => setQuantidadeAssoc(e.target.value)}
                          inputProps={{ min: 2, step: 1, inputMode: "numeric" }}
                          placeholder="Ex.: 12"
                        />
                      </MDBox>
                    )}

                    {tipoConversaoAssoc === "C" && (
                      <MDBox mt={1.5}>
                        <MDTypography variant="button" display="block" mb={1}>
                          Quantas unidades do material selecionado contém este EAN?
                        </MDTypography>

                        <TextField
                          size="small"
                          fullWidth
                          type="number"
                          value={quantidadeAssoc}
                          onChange={(e) => setQuantidadeAssoc(e.target.value)}
                          inputProps={{ min: 2, step: 1, inputMode: "numeric" }}
                          placeholder="Ex.: 6"
                        />
                      </MDBox>
                    )}

                    <MDBox mt={1.5}>
                      <MDTypography variant="caption" color="text" display="block">
                        Quantidade contabilizada por leitura:
                      </MDTypography>

                      <MDTypography variant="button" fontWeight="bold" color="info">
                        {(() => {
                          const n = Number(String(quantidadeAssoc).replace(",", "."));

                          if (tipoConversaoAssoc === "A") {
                            return "1 unidade do material selecionado";
                          }

                          if (!Number.isInteger(n) || n <= 1) {
                            return "Indica um número inteiro superior a 1";
                          }

                          if (tipoConversaoAssoc === "B") {
                            return `${(1 / n).toLocaleString("pt-PT", {
                              maximumFractionDigits: 12,
                            })} unidade(s) da referência`;
                          }

                          return `${n} unidades da referência`;
                        })()}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </MDBox>
            ) : (!onMaterialDetected || permitirAssociacoes) && codigo ? (
              <MDBox p={1.5} borderRadius="8px" style={{ background: "#fff7ed" }}>
                <MDButton
                  color="info"
                  variant="outlined"
                  fullWidth
                  onClick={() => setMostrarNovaAssociacao(true)}
                >
                  Criar nova associação
                </MDButton>
              </MDBox>
            ) : null}
          </MDBox>
        </DialogContent>

        <DialogActions
          sx={{
            flexWrap: "wrap",
            gap: 1,
            justifyContent: "flex-end",
            "& > :not(style) ~ :not(style)": {
              marginLeft: 0,
            },
          }}
        >
          <MDButton onClick={fechar} color="secondary">
            Fechar
          </MDButton>

          {(!onMaterialDetected || permitirAssociacoes) &&
            codigo.trim() &&
            mostrarNovaAssociacao &&
            materialSelecionado &&
            ["A", "B", "C"].includes(tipoConversaoAssoc) &&
            (tipoConversaoAssoc === "A" ||
              (Number.isSafeInteger(Number(String(quantidadeAssoc).trim())) &&
                String(quantidadeAssoc).trim() !== "" &&
                Number(quantidadeAssoc) > 1 &&
                Number(quantidadeAssoc) <= 2147483647)) && (
              <MDButton
                color="success"
                onClick={associarCodigoMaterial}
                disabled={associarLoading || loading}
              >
                {associarLoading ? "A associar..." : "Associar código ao material"}
              </MDButton>
            )}

          <MDButton
            onClick={() => {
              codigoInputRef.current?.focus();
              pesquisarCodigo();
            }}
            color="info"
            disabled={loading}
          >
            Pesquisar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openConfirmarSubstituicao}
        onClose={() => {
          if (!associarLoading) {
            setOpenConfirmarSubstituicao(false);
            setDadosSubstituicao(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Código já existente</DialogTitle>

        <DialogContent>
          <MDTypography variant="body2" color="text">
            O material selecionado já tem um código ativo deste tipo:
          </MDTypography>

          <MDBox mt={1} p={1.5} borderRadius="8px" style={{ background: "#f8f9fa" }}>
            <MDTypography variant="button" display="block">
              Tipo: <b>{dadosSubstituicao?.existente?.tipo_codigo || ""}</b>
            </MDTypography>
            <MDTypography variant="button" display="block">
              Código atual: <b>{dadosSubstituicao?.existente?.codigo || ""}</b>
            </MDTypography>
            <MDTypography variant="button" display="block">
              Novo código: <b>{dadosSubstituicao?.novo?.codigo || ""}</b>
            </MDTypography>
          </MDBox>

          <MDTypography variant="caption" color="text" display="block" mt={1.5}>
            Confirma que pretende substituir o código existente pelo novo?
          </MDTypography>
        </DialogContent>

        <DialogActions>
          <MDButton
            color="secondary"
            disabled={associarLoading}
            onClick={() => {
              setOpenConfirmarSubstituicao(false);
              setDadosSubstituicao(null);
            }}
          >
            Cancelar
          </MDButton>

          <MDButton
            color="warning"
            disabled={associarLoading || !dadosSubstituicao?.novo}
            onClick={confirmarSubstituicao}
          >
            {associarLoading ? "A substituir..." : "Substituir"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(identificadorEditar)}
        onClose={() => {
          if (!guardarQuantidadeLoading) {
            setIdentificadorEditar(null);
            setQuantidadeEditar("");
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Editar conversão do EAN</DialogTitle>

        <DialogContent>
          <MDBox pt={1} display="grid" gap={2}>
            <MDTypography variant="body2" color="text">
              Código de Barras: <b>{identificadorEditar?.codigo || ""}</b>
            </MDTypography>

            <MDTypography variant="caption" color="text">
              Tipo: {String(identificadorEditar?.tipo_codigo || "").toUpperCase()}
            </MDTypography>

            <MDTypography variant="body2" fontWeight="medium" sx={{ overflowWrap: "anywhere" }}>
              {identificadorEditar?.nomematerial || ""}
            </MDTypography>

            <MDBox>
              <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
                O que representa este EAN?
              </MDTypography>

              <RadioGroup
                value={tipoConversaoEditar}
                onChange={(e) => {
                  const tipo = e.target.value;

                  setTipoConversaoEditar(tipo);
                  setQuantidadeEditar(tipo === "A" ? "1" : "");
                }}
              >
                <FormControlLabel
                  value="A"
                  control={<Radio size="small" />}
                  label="A — Material selecionado completo"
                />

                <FormControlLabel
                  value="B"
                  control={<Radio size="small" />}
                  label="B — Material selecionado incompleto"
                />

                <FormControlLabel
                  value="C"
                  control={<Radio size="small" />}
                  label="C — Conjunto de unidades do material selecionado"
                />
              </RadioGroup>

              {tipoConversaoEditar !== "A" && (
                <MDBox mt={2}>
                  <MDTypography variant="button" display="block" mb={1}>
                    {tipoConversaoEditar === "B"
                      ? "Quantos artigos deste EAN são necessários para formar 1 unidade do material selecionado?"
                      : "Quantas unidades do material selecionado contém este EAN?"}
                  </MDTypography>

                  <TextField
                    type="number"
                    size="small"
                    fullWidth
                    autoFocus
                    value={quantidadeEditar}
                    onChange={(e) => setQuantidadeEditar(e.target.value)}
                    inputProps={{ min: 2, step: 1, inputMode: "numeric" }}
                    disabled={guardarQuantidadeLoading}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </MDBox>
              )}

              <MDTypography variant="caption" color="text" display="block" mt={2}>
                {tipoConversaoEditar === "A"
                  ? "Cada leitura equivale a 1 unidade do material selecionado."
                  : tipoConversaoEditar === "B"
                  ? "O EAN identifica uma parte da unidade do material selecionado."
                  : "O EAN identifica várias unidades completas do material selecionado."}
              </MDTypography>
            </MDBox>
          </MDBox>
        </DialogContent>

        <DialogActions
          sx={{
            flexWrap: "wrap",
            gap: 1,
            justifyContent: "flex-end",
          }}
        >
          <MDButton
            color="error"
            variant="outlined"
            disabled={guardarQuantidadeLoading || removerAssociacaoLoading}
            onClick={() => setConfirmarRemocaoAssociacao(true)}
            sx={{ mr: { sm: "auto" } }}
          >
            Remover associação
          </MDButton>

          <MDButton
            color="secondary"
            disabled={guardarQuantidadeLoading || removerAssociacaoLoading}
            onClick={() => {
              setIdentificadorEditar(null);
              setQuantidadeEditar("");
            }}
          >
            Cancelar
          </MDButton>

          <MDButton
            color="info"
            disabled={guardarQuantidadeLoading}
            onClick={guardarQuantidadeIdentificador}
          >
            {guardarQuantidadeLoading ? "A guardar..." : "Guardar"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmarRemocaoAssociacao}
        onClose={() => {
          if (!removerAssociacaoLoading) {
            setConfirmarRemocaoAssociacao(false);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remover associação EAN</DialogTitle>

        <DialogContent dividers>
          <MDBox display="grid" gap={1.5}>
            <MDTypography variant="body2">Pretendes remover a seguinte associação?</MDTypography>

            <MDBox p={1.5} borderRadius="8px" sx={{ backgroundColor: "#f8f9fa" }}>
              <MDTypography variant="caption" color="text" display="block">
                Código EAN
              </MDTypography>

              <MDTypography variant="button" fontWeight="bold" display="block">
                {identificadorEditar?.codigo || "—"}
              </MDTypography>

              <MDTypography variant="caption" color="text" display="block" mt={1.5}>
                Material associado
              </MDTypography>

              <MDTypography
                variant="button"
                fontWeight="bold"
                display="block"
                sx={{ overflowWrap: "anywhere" }}
              >
                {identificadorEditar?.nomematerial || identificadorEditar?.refmaterial || "—"}
              </MDTypography>
            </MDBox>

            <MDTypography variant="body2" color="error">
              A associação ficará inativa e deixará de aparecer nas pesquisas. As restantes
              associações deste EAN não serão alteradas.
            </MDTypography>
          </MDBox>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5, gap: 1, flexWrap: "wrap" }}>
          <MDButton
            color="secondary"
            variant="outlined"
            disabled={removerAssociacaoLoading}
            onClick={() => setConfirmarRemocaoAssociacao(false)}
          >
            Cancelar
          </MDButton>

          <MDButton
            color="error"
            disabled={removerAssociacaoLoading}
            onClick={removerAssociacaoIdentificador}
          >
            {removerAssociacaoLoading ? "A remover..." : "Confirmar remoção"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <MenuSearchMateriais
        open={openMaterialDialog}
        onClose={() => {
          setOpenMaterialDialog(false);
          setMaterialDialog(null);
          fechar();
        }}
        material={materialDialog}
      />
    </>
  );
}
